import Link from 'next/link';

import { getFormattedNumber } from '@hub/common/src/format-utils';
import AccountTheme from '@hub/common/src/AccountTheme';

import { Account, CitOrigin } from '@hub/common/src/types/cit-public-api';

export default function CollectionCard({
  account,
  citOrigin,
  size,
  handleShowToolTip,
  handleHideToolTip
}: {
  account: Account;
  citOrigin: CitOrigin;
  // the card's width, given by the virtualizer that lays these out. The 4:2 cover image and the
  // text block below it size themselves off it
  size?: number;
  handleShowToolTip?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  handleHideToolTip?: () => void;
}) {
  // one card per account, so each needs its own theme rather than the page's. A test theme never
  // applies to the directory, where no single account owns the page
  const accountTheme = new AccountTheme(citOrigin, account);

  // an account that hid a field for this origin gets an empty value back, so having a value is the
  // only check each line needs
  const total = accountTheme.total();
  const location = accountTheme.location();
  const description = accountTheme.description();
  const avatarURL = accountTheme.avatarURL();

  return (
    <Link
      key={account.id}
      href={`/${account.slug || account.id}`}
      className='group bg-white rounded-lg shadow-xs hover:shadow-md transition-all duration-200 overflow-hidden'
      style={{ width: size, height: size ? '100%' : undefined }}
      prefetch={false}
      onMouseEnter={handleShowToolTip}
      onMouseLeave={handleHideToolTip}
      data-tooltip-content={accountTheme.accountName()}
      data-tooltip-id={account.id}
    >
      <div className='aspect-4/2 relative group-hover:scale-105 transition-transform duration-200'>
        {/* an <img> rather than a CSS background so it can be lazy: a card that never scrolls into
            view costs no fetch and no decoded bitmap, which matters on the directory's long list */}
        <img
          src={accountTheme.backgroundURL()}
          alt={`${accountTheme.accountName()} background image`}
          loading='lazy'
          decoding='async'
          className='absolute inset-0 w-full h-full object-cover object-center'
        />
        {avatarURL && (
          <div className='absolute -bottom-6 left-4'>
            <img
              src={avatarURL}
              alt={`${accountTheme.accountName()} avatar`}
              loading='lazy'
              className='w-12 h-12 rounded-full border-2 border-white aspect-square'
            />
          </div>
        )}
      </div>
      <div className='p-3 sm:p-4'>
        <h3 className='text-base sm:text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors mt-4'>
          <span className='line-clamp-1'>{accountTheme.accountName()}</span>
        </h3>
        <div className='mt-1'>
          {total ? (
            <p className='text-xs sm:text-sm text-gray-600'>
              {`${getFormattedNumber(total)} items`}
            </p>
          ) : null}
          {location ? <p className='mt-1 text-xs sm:text-sm text-gray-500'>{location}</p> : null}
          {description ? (
            <p className='mt-2 text-xs sm:text-sm text-gray-600 line-clamp-2'>{description}</p>
          ) : null}
        </div>
      </div>
    </Link>
  );
}
